import { useState } from 'react';
import { useParams } from 'react-router';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui/buttons/Button';
import { BackButton } from '~/components/ui/buttons/BackButton';
import OrganizationUsersCard from '~/components/common/OrganizationUsersCard';
import { userPhoneAndLastEditCells } from '~/components/common/userLastEditCells';
import {
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import {
  useGetOrganization,
  useReactivateTenantAdminForOrg,
} from '~/api/organizations';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import AddTenantAdminModal from './sections/AddTenantAdminModal';
import { OrganizationContactCard } from './sections/OrganizationContactCard';
import {
  DeactivateTenantAdminDialog,
  type TenantAdminRowData,
} from './sections/DeactivateTenantAdminDialog';

export default function AdminOrganizationDetail() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { t } = useTranslation(['organizations', 'users']);

  const [addTenantAdminModalOpen, setAddTenantAdminModalOpen] = useState(false);
  const [adminToDeactivate, setAdminToDeactivate] =
    useState<TenantAdminRowData | null>(null);

  const { data, isLoading } = useGetOrganization(organizationId!);
  const organization = data?.organization ?? null;

  const { mutate: reactivate } = useReactivateTenantAdminForOrg();

  const tenantAdminsColumns: TableColumn[] = [
    { key: 'name', header: t('organizations.detail.table.name'), width: 20 },
    { key: 'email', header: t('organizations.detail.table.email'), width: 24 },
    {
      key: 'phoneNumber',
      header: t('organizations.detail.table.phoneNumber'),
      width: 12,
    },
    {
      key: 'status',
      header: t('organizations.detail.table.status'),
      width: 12,
    },
    {
      key: 'lastEditedBy',
      header: t('organizations.detail.table.lastEditedBy'),
      width: 14,
    },
    {
      key: 'lastEditedAt',
      header: t('organizations.detail.table.lastEditedAt'),
      width: 14,
    },
    {
      key: 'actions',
      header: t('organizations.detail.table.actions'),
      width: 16,
      stickTo: 'right',
    },
  ];

  const tableControls = useTable({});

  const rows: TableRow[] = (organization?.tenantAdmins ?? []).map((admin) => {
    const isDisabled = admin.status === 'DISABLED';
    return {
      id: { value: admin.id },
      name: { value: `${admin.firstName} ${admin.lastName}` },
      email: { value: admin.externalId },
      status: { value: t(`users.status.${admin.status}`, { ns: 'users' }) },
      ...userPhoneAndLastEditCells(admin),
      actions: {
        value: (
          <div className="flex gap-2">
            {isDisabled ? (
              <Button
                size="sm"
                width="auto"
                variant="ghost-primary"
                onClick={() =>
                  reactivate(
                    { userId: admin.id },
                    {
                      onSuccess: () =>
                        toast.success(
                          t('organizations.toast.adminReactivated')
                        ),
                      onError: (error: ClientError) =>
                        toast.error(
                          extractGraphQLErrorMessages(error).join(', ')
                        ),
                    }
                  )
                }
              >
                {t('organizations.action.reactivate')}
              </Button>
            ) : (
              <Button
                size="sm"
                width="auto"
                variant="destructive-outline"
                onClick={() =>
                  setAdminToDeactivate({
                    id: admin.id,
                    firstName: admin.firstName,
                    lastName: admin.lastName,
                  })
                }
              >
                {t('organizations.action.deactivate')}
              </Button>
            )}
          </div>
        ),
      },
    };
  });

  const organizationName =
    organization?.name ?? organizationId ?? 'Organization';

  return (
    <>
      <AddTenantAdminModal
        open={addTenantAdminModalOpen}
        onClose={() => setAddTenantAdminModalOpen(false)}
        organizationId={organizationId!}
      />
      <DeactivateTenantAdminDialog
        admin={adminToDeactivate}
        onClose={() => setAdminToDeactivate(null)}
      />

      <div className="w-full flex flex-col gap-6">
        <BackButton
          variant="subtle"
          link="/admin/organizations"
          label={t('organizations.detail.back')}
        />

        {/* Page header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-neut-prim">
            {isLoading ? '...' : organizationName}
          </h1>
        </div>

        {organization && (
          <OrganizationContactCard organization={organization} />
        )}

        <OrganizationUsersCard
          title={t('organizations.detail.tenantAdmins')}
          addLabel={t('organizations.detail.addTenantAdmin')}
          onAdd={() => setAddTenantAdminModalOpen(true)}
          columns={tenantAdminsColumns}
          rows={rows}
          tableControls={tableControls}
          emptyMessage={t('organizations.detail.empty')}
          tableName="tenant-admins"
        />
      </div>
    </>
  );
}
